/**
 * Audit-log query hooks (Phase 13 Cluster A — Feature D).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useActiveTenantId } from './useTenantContext';
import type { TenantKey } from '../services/api/keys';
import type { AuditAction, AuditEntityType, AuditListQuery } from '@armard/shared';

export interface AuditListEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: AuditAction;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  timestamp: string;
  changedFields: string[] | null;
}

export interface AuditDetailEntry extends AuditListEntry {
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface AuditListResult {
  entries: AuditListEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const auditKeys = {
  all: (t: TenantKey) => ['audit', t] as const,
  lists: (t: TenantKey) => [...auditKeys.all(t), 'list'] as const,
  list: (t: TenantKey, q: Partial<AuditListQuery>) => [...auditKeys.lists(t), q] as const,
  forEntity: (
    t: TenantKey,
    entityType: AuditEntityType,
    entityId: string,
    q: Partial<AuditListQuery>
  ) => [...auditKeys.all(t), 'entity', entityType, entityId, q] as const,
  detail: (t: TenantKey, id: string) => [...auditKeys.all(t), 'detail', id] as const,
};

function buildSearch(query: Partial<AuditListQuery>): string {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.entityType) params.set('entityType', query.entityType);
  if (query.entityId) params.set('entityId', query.entityId);
  if (query.action) params.set('action', query.action);
  if (query.userId) params.set('userId', query.userId);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useAuditList(query: Partial<AuditListQuery> = {}) {
  const tenantId = useActiveTenantId();
  const fullQuery = { page: query.page ?? 1, limit: query.limit ?? 40, ...query };
  return useQuery({
    queryKey: auditKeys.list(tenantId, fullQuery),
    queryFn: () => api.get<AuditListResult>(`/audit${buildSearch(fullQuery)}`),
    enabled: tenantId !== null,
    staleTime: 15_000,
  });
}

export function useAuditForEntity(
  entityType: AuditEntityType,
  entityId: string,
  query: Partial<AuditListQuery> = {},
  enabled = true
) {
  const tenantId = useActiveTenantId();
  const fullQuery = { page: query.page ?? 1, limit: query.limit ?? 20, ...query };
  return useQuery({
    queryKey: auditKeys.forEntity(tenantId, entityType, entityId, fullQuery),
    queryFn: () =>
      api.get<AuditListResult>(
        `/audit/entity/${entityType}/${entityId}${buildSearch(fullQuery)}`
      ),
    enabled: enabled && !!entityId && tenantId !== null,
    staleTime: 15_000,
  });
}

export function useAuditEntry(id: string, enabled = true) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: auditKeys.detail(tenantId, id),
    queryFn: () => api.get<AuditDetailEntry>(`/audit/${id}`),
    enabled: enabled && !!id && tenantId !== null,
    staleTime: 60_000,
  });
}

// Display helpers

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  create: 'Erstellt',
  update: 'Geändert',
  delete: 'Gelöscht',
};

// Sprint 006 A10 — die 002-Ära schreibt weitere Actions (varchar, kein
// Enum im Frontend-Typ). Ohne Label landeten rohe Keys wie
// 'hash_mismatch' als User-Text im Timeline-Copy.
export const EXTENDED_ACTION_LABELS: Record<string, string> = {
  ...AUDIT_ACTION_LABELS,
  erech_mismatch: 'eRechnung-Abweichung festgestellt',
  migration_start: 'Storage-Migration gestartet',
  hash_mismatch: 'Integritäts-Fehler festgestellt',
  upd_closed_qt: 'Im geschlossenen Quartal geändert',
  export_failed: 'Export fehlgeschlagen',
  merge: 'Zusammengeführt',
  xtenant_export: 'Mandanten-übergreifender Export-Versuch',
  summencheck: 'Summen-Check',
  recog_learn: 'Erkennungs-Schema gelernt',
  sum_override: 'Summen-Override bestätigt',
  ocr_llm_fail: 'KI-Erkennung fehlgeschlagen',
  ocr_llm_merge: 'KI-Erkennung übernommen',
  // 003 decision 2026-10-18 — own invoice settled / reverted by a confirmed bank match.
  paid_via_bank_match: 'Per Bankabgleich als bezahlt markiert',
  bank_match_unpaid: 'Bankabgleich aufgehoben, Status zurückgesetzt',
};

export function auditActionLabel(action: string): string {
  return EXTENDED_ACTION_LABELS[action] ?? action;
}

export const AUDIT_ACTION_COLORS: Record<AuditAction, string> = {
  create: 'bg-green-500/15 text-green-300 border-green-500/20',
  update: 'bg-blue-500/15 text-blue-300 border-blue-500/20',
  delete: 'bg-red-500/15 text-red-300 border-red-500/20',
};

export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  user: 'Benutzer',
  customer: 'Kunde',
  address: 'Adresse',
  invoice: 'Rechnung',
  invoice_item: 'Rechnungsposten',
  payment_reminder: 'Mahnung',
  contract: 'Vertrag',
  contract_item: 'Vertragsposten',
  cost_estimate: 'Angebot',
  estimate_item: 'Angebots-Posten',
  settings: 'Einstellung',
  // 002-Ära (Sprint 006 A10 — vorher rohe Keys im UI)
  expense: 'Beleg',
  expense_item: 'Beleg-Position',
  expense_attachment: 'Beleg-Anhang',
  expense_category: 'Kategorie',
  expense_comment: 'Beleg-Kommentar',
  expense_revision: 'Beleg-Revision',
  expense_status: 'Beleg-Status',
  supplier: 'Lieferant',
  depreciation_plan: 'Abschreibungsplan',
  depreciation_event: 'Abschreibungs-Ereignis',
  export_job: 'Export',
  storage_migration: 'Storage-Migration',
  tenant: 'Mandant',
  tenant_settings: 'Mandanten-Einstellung',
  tenant_quartal_status: 'Quartals-Status',
  admin_action_mirror: 'Admin-Aktion',
};

export function auditEntityLabel(entityType: string): string {
  return AUDIT_ENTITY_LABELS[entityType] ?? entityType;
}

export function auditUserLabel(entry: Pick<AuditListEntry, 'userName' | 'userEmail' | 'userId'>): string {
  return entry.userName || entry.userEmail || entry.userId || 'Unbekannt';
}
